import { z } from 'zod';
/**
 * Built-in Handlebars block helpers that take a variable as first argument.
 * @see https://handlebarsjs.com/guide/builtin-helpers.html
 */
const BLOCK_HELPERS_WITH_VARIABLE = new Set(['if', 'unless', 'each', 'with']);
/**
 * Built-in Handlebars helpers that don't take variables (or handle them specially).
 */
const BUILT_IN_HELPERS = new Set(['else', 'lookup', 'log']);
/**
 * Custom helpers registered in the mailer package.
 * These take values as arguments but are helper names, not variables.
 */
const CUSTOM_HELPERS = new Set(['uppercase', 'lowercase', 'capitalize', 'default', 'eq', 'neq']);
/**
 * Special Handlebars keywords and loop variables that should not be validated.
 * @see https://handlebarsjs.com/guide/builtin-helpers.html#each
 */
const SPECIAL_VARIABLES = new Set([
    'this', // Current context in #each
    'else', // Else block
]);
/**
 * Checks if a variable name is a special Handlebars loop variable.
 * These start with @ and include: @index, @key, @first, @last, @../index, etc.
 */
function isLoopVariable(name) {
    return name.startsWith('@');
}
/**
 * Extracts variable names from a Handlebars template string.
 * Handles all common patterns including:
 * - Simple variables: `{{variableName}}`
 * - Unescaped: `{{{variableName}}}`
 * - Paths: `{{user.name}}` (extracts 'user')
 * - Block helpers: `{{#if condition}}`, `{{#unless condition}}`, `{{#each items}}`, `{{#with object}}`
 * - Custom helpers: `{{uppercase name}}`, `{{#eq status "active"}}`, etc.
 * - Loop variables: `{{@index}}`, `{{@key}}`, `{{@first}}`, `{{@last}}` (skipped)
 * - Special: `{{this}}`, `{{else}}` (skipped)
 *
 * @param template - The template string to parse
 * @returns Array of unique variable names found in the template
 * @see https://handlebarsjs.com/guide/builtin-helpers.html
 */
export function extractTemplateVariables(template) {
    if (!template)
        return [];
    const variables = new Set();
    // Match handlebars expressions: {{...}} or {{{...}}}
    const regex = /\{\{+([^}]+)\}\}+/g;
    let match;
    while ((match = regex.exec(template)) !== null) {
        const content = match[1].trim();
        // Skip comments (start with !) and partials (start with >)
        if (content.startsWith('!') || content.startsWith('>'))
            continue;
        // Skip closing block tags (e.g., {{/if}}, {{/each}})
        if (content.startsWith('/'))
            continue;
        // Handle block helpers like {{#if condition}} or {{#each items}}
        if (content.startsWith('#')) {
            const parts = content.slice(1).trim().split(/\s+/);
            const helperName = parts[0];
            // If it's a block helper that takes a variable, extract it
            if (BLOCK_HELPERS_WITH_VARIABLE.has(helperName) && parts.length >= 2) {
                const varExpression = parts[1];
                extractVariableFromExpression(varExpression, variables);
            }
            // For custom block helpers like {{#eq var "value"}}, extract the first argument
            else if (CUSTOM_HELPERS.has(helperName) && parts.length >= 2) {
                const varExpression = parts[1];
                extractVariableFromExpression(varExpression, variables);
            }
            continue;
        }
        // Handle inline helpers like {{uppercase name}} or {{default name "Guest"}}
        const parts = content.split(/\s+/);
        if (parts.length > 1 && CUSTOM_HELPERS.has(parts[0])) {
            // Extract variable from helper argument (skip string literals)
            const arg = parts[1];
            if (!arg.startsWith('"') && !arg.startsWith("'")) {
                extractVariableFromExpression(arg, variables);
            }
            continue;
        }
        // Handle lookup helper: {{lookup array @index}}
        if (parts[0] === 'lookup' && parts.length >= 2) {
            extractVariableFromExpression(parts[1], variables);
            continue;
        }
        // Regular variable or path expression
        extractVariableFromExpression(content, variables);
    }
    return Array.from(variables);
}
/**
 * Extracts the root variable from an expression and adds it to the set.
 * Handles paths like `user.name` (extracts 'user') and skips special variables.
 */
function extractVariableFromExpression(expression, variables) {
    // Skip special variables like @index, @key, @first, @last
    if (isLoopVariable(expression))
        return;
    // Get the root variable (before any dots or whitespace)
    const rootVariable = expression.split('.')[0].split(/\s/)[0];
    // Skip special Handlebars keywords
    if (SPECIAL_VARIABLES.has(rootVariable))
        return;
    // Skip built-in helpers used as expressions
    if (BUILT_IN_HELPERS.has(rootVariable))
        return;
    // Validate it's a proper identifier (starts with letter or underscore)
    if (rootVariable && /^[a-zA-Z_]\w*$/.test(rootVariable)) {
        variables.add(rootVariable);
    }
}
/**
 * Validates that all template variables in a string are in the allowed list.
 * @param value - The template string to validate
 * @param allowedVariables - Array of allowed variable keys
 * @returns Validation result with isValid boolean and invalidVariables array
 */
export function validateTemplateVariables(value, allowedVariables) {
    if (!value)
        return { isValid: true, invalidVariables: [] };
    const usedVariables = extractTemplateVariables(value);
    const invalidVariables = usedVariables.filter((v) => !allowedVariables.includes(v));
    return {
        isValid: invalidVariables.length === 0,
        invalidVariables,
    };
}
/**
 * Base schema for upserting a custom email template (without variable validation)
 */
export const customEmailTemplateUpsertSchema = z.object({
    subject: z.string('Subject is required').min(1, 'Subject is required'),
    greeting: z.string().nullish(),
    body: z.string('Body is required').min(1, 'Body is required'),
    buttonText: z.string().nullish(),
    isActive: z.boolean().optional(),
    isDisabled: z.boolean().optional(),
});
/**
 * Global variables available in all email templates.
 * These are automatically included in validation.
 */
export const GLOBAL_EMAIL_VARIABLES = [
    {
        key: 'governmentName',
        description: 'The name of the government',
        example: 'City of Gotham',
    },
    {
        key: 'recipientFirstName',
        description: 'The first name of the recipient',
        example: 'John',
    },
    {
        key: 'recipientLastName',
        description: 'The last name of the recipient',
        example: 'Doe',
    },
    {
        key: 'recipientEmail',
        description: 'The email address of the recipient',
        example: 'john.doe@example.com',
    },
];
/**
 * Creates a custom email template upsert schema with Handlebars variable validation.
 * Validates that all template variables used in are present in the allowed variables list.
 *
 * @param allowedVariables - Array of EmailTemplateVariable objects defining valid variables
 * @returns Zod schema with template variable validation
 *
 * @example
 * ```typescript
 * const schema = createCustomEmailTemplateUpsertSchema([
 *   { key: 'userName', description: 'User name', example: 'John' },
 *   { key: 'resetLink', description: 'Password reset link', example: 'https://...' },
 * ]);
 *
 * // This will pass validation
 * schema.parse({ subject: 'Hello {{userName}}', body: 'Click {{resetLink}}' });
 *
 * // This will fail validation (invalidVar is not in the allowed list)
 * schema.parse({ subject: 'Hello {{invalidVar}}', body: 'Content' });
 * ```
 */
export function createCustomEmailTemplateUpsertSchema(allowedVariables) {
    // Combine global and email-specific variables
    const allowedKeys = [...GLOBAL_EMAIL_VARIABLES, ...allowedVariables].map((v) => v.key);
    return customEmailTemplateUpsertSchema.superRefine((data, ctx) => {
        const fieldsToValidate = [
            { field: 'subject', value: data.subject },
            { field: 'greeting', value: data.greeting },
            { field: 'body', value: data.body },
            { field: 'buttonText', value: data.buttonText },
        ];
        for (const { field, value } of fieldsToValidate) {
            const { isValid, invalidVariables } = validateTemplateVariables(value, allowedKeys);
            if (!isValid) {
                ctx.addIssue({
                    code: 'custom',
                    path: [field],
                    message: `Invalid template variable(s): ${invalidVariables.join(', ')}. Allowed variables: ${allowedKeys.join(', ')}`,
                });
            }
        }
    });
}
